import { useEffect, useState } from "react";
import api, { downloadFile, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Building2, Wallet, Receipt, Percent, TrendingUp, Calendar, Wrench } from "lucide-react";
import { toast } from "sonner";

function brl(n) {
  return (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function monthLabel(comp) {
  if (!comp) return "—";
  const [y, m] = comp.split("-");
  const names = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${names[parseInt(m, 10) - 1] || m}/${y}`;
}

function startOfMonthISO(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function endOfMonthISO(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

export default function ControleBvsmPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(startOfMonthISO());
  const [dateTo, setDateTo] = useState(endOfMonthISO());
  const [hospitals, setHospitals] = useState([]);
  const [hospitalId, setHospitalId] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (hospitalId) params.hospital_id = hospitalId;
      const r = await api.get("/bvsm/control", { params });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.get("/hospitals").then((r) => setHospitals(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hospitalId]);

  function setCurrentMonth() {
    setDateFrom(startOfMonthISO());
    setDateTo(endOfMonthISO());
    setTimeout(load, 0);
  }

  function setPreviousMonth() {
    const d = new Date();
    const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    setDateFrom(startOfMonthISO(prev));
    setDateTo(endOfMonthISO(prev));
    setTimeout(load, 0);
  }

  async function exportXlsx() {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (hospitalId) params.hospital_id = hospitalId;
      await downloadFile(
        "/bvsm/control.xlsx",
        `controle_bvsm_${dateFrom}_a_${dateTo}.xlsx`,
        params,
      );
      toast.success("Planilha gerada.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const t = data?.totals || {};
  const settings = data?.settings || {};
  const taxPct = (settings.bvsm_estimated_tax_rate || 0) * 100;
  const tiers = settings.apex_commission_tiers || [];

  return (
    <div className="space-y-5" data-testid="bvsm-control-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Building2 className="h-7 w-7 text-blue-700" />
            Controle BVSM
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Visão administrativa: faturamento × repasse aos cirurgiões × impostos × comissão Apex
          </p>
        </div>
        <Button onClick={exportXlsx} className="bg-blue-700 hover:bg-blue-800 h-11 md:h-9 w-full sm:w-auto" data-testid="btn-export-bvsm">
          <Download className="h-4 w-4 mr-2" /> Exportar Excel
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">De</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="w-full sm:w-44 mt-1" data-testid="filter-date-from" />
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Até</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="w-full sm:w-44 mt-1" data-testid="filter-date-to" />
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Hospital</Label>
              <select value={hospitalId} onChange={(e) => setHospitalId(e.target.value)}
                className="w-full sm:w-56 block mt-1 border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white min-h-[44px]"
                data-testid="filter-hospital">
                <option value="">Todos</option>
                {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap gap-2 sm:ml-auto w-full sm:w-auto">
              <Button variant="outline" type="button" onClick={setPreviousMonth} data-testid="btn-prev-month" className="h-11 md:h-9 flex-1 sm:flex-initial">
                <Calendar className="h-4 w-4 mr-1" /> Mês anterior
              </Button>
              <Button variant="outline" type="button" onClick={setCurrentMonth} data-testid="btn-current-month" className="h-11 md:h-9 flex-1 sm:flex-initial">
                <Calendar className="h-4 w-4 mr-1" /> Mês atual
              </Button>
              <Button onClick={load} className="bg-slate-800 hover:bg-slate-900 h-11 md:h-9 flex-1 sm:flex-initial" data-testid="btn-apply-filters">Aplicar</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <KPI icon={<Wallet />} label="Total faturado" value={brl(t.total_faturado)} tone="slate" testid="kpi-total-faturado" />
        <KPI icon={<Receipt />} label="Repasse cirurgiões" value={brl(t.repasse_cirurgioes)} tone="amber" testid="kpi-repasse" />
        <KPI icon={<Percent />} label={`Imposto BVSM (${taxPct.toFixed(2)}%)`} value={brl(t.imposto_bvsm)} tone="rose" testid="kpi-imposto" />
        <KPI icon={<Building2 />} label="Comissão Apex" value={brl(t.comissao_apex)} tone="violet" testid="kpi-apex" />
        <KPI icon={<Wrench />} label="Instrumentadores" value={brl(t.custo_instrumentador)} tone="cyan" testid="kpi-instrumentador" />
        <KPI
          icon={<TrendingUp />}
          label={`Resultado BVSM (${(t.resultado_bvsm_pct ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%)`}
          value={brl(t.resultado_bvsm)}
          tone="emerald"
          testid="kpi-resultado"
        />
      </div>

      <div className="text-xs px-3 py-2 rounded-md bg-blue-50 text-blue-800 border border-blue-200" data-testid="bvsm-rules-hint">
        <strong>Como é calculado:</strong> Resultado BVSM = Total faturado − Repasse cirurgiões − Imposto BVSM ({taxPct.toFixed(2)}% do total faturado) − Comissão Apex (progressiva sobre o faturamento bruto MENSAL — zera todo dia 01) − Custo de Instrumentadores (por turno trabalhado, valor configurável por categoria).
      </div>

      {/* Custo Instrumentadores breakdown */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Wrench className="h-5 w-5 text-cyan-700" />
              Custo de Instrumentadores
            </h2>
            <span className="text-xs text-slate-500">Por hospital × turno · padrão global em <em>Configurações</em>, override em <em>Hospitais</em></span>
          </div>
          {(data?.tech_cost_by_hospital || []).length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-8">Sem turnos com cirurgia no período.</div>
          ) : (
            <div className="space-y-4">
              {(data?.tech_cost_by_hospital || []).map((h) => {
                const isPerDay = h.model === "per_day";
                return (
                <div key={h.hospital_id || h.hospital_name} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`tech-hospital-${h.hospital_id || 'none'}`}>
                  <div className="bg-slate-50 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{h.hospital_name || "—"}</span>
                      {isPerDay && (
                        <span className="inline-block text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`tech-hospital-${h.hospital_id}-model-badge`}>
                          Diária
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600">
                      Subtotal: <strong className="text-cyan-700" data-testid={`tech-hospital-${h.hospital_id || 'none'}-total`}>{brl(h.total)}</strong>
                    </div>
                  </div>
                  {isPerDay ? (
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="text-left px-4 py-2">Modelo</th>
                          <th className="text-right px-4 py-2">Valor por dia</th>
                          <th className="text-right px-4 py-2">Dias com cirurgia</th>
                          <th className="text-right px-4 py-2">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const info = h.breakdown?.per_day || {};
                          return (
                            <tr className="border-t border-slate-100">
                              <td className="px-4 py-2">{info.label || "Diária"}</td>
                              <td className="px-4 py-2 text-right">{brl(info.rate)}</td>
                              <td className="px-4 py-2 text-right">{info.turnos || 0}</td>
                              <td className="px-4 py-2 text-right font-medium">{brl(info.valor)}</td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2">Tipo de turno</th>
                        <th className="text-right px-4 py-2">Valor por turno</th>
                        <th className="text-center px-4 py-2">Fonte</th>
                        <th className="text-right px-4 py-2">Turnos no período</th>
                        <th className="text-right px-4 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(h.breakdown || {}).map(([key, info]) => (
                        <tr key={key} className={`border-t border-slate-100 ${info.turnos > 0 ? "" : "text-slate-400"}`} data-testid={`tech-row-${h.hospital_id || 'none'}-${key}`}>
                          <td className="px-4 py-2">{info.label}</td>
                          <td className="px-4 py-2 text-right">{brl(info.rate)}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${info.source === "hospital" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"}`}>
                              {info.source === "hospital" ? "Hospital" : "Global"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">{info.turnos}</td>
                          <td className="px-4 py-2 text-right font-medium">{brl(info.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  )}
                </div>
                );
              })}
              <div className="flex items-center justify-end px-4 pt-2 border-t-2 border-slate-300">
                <div className="text-sm text-slate-700">Total geral: </div>
                <div className="ml-2 font-bold text-cyan-700 text-lg" data-testid="tech-total">{brl(t.custo_instrumentador)}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Apex breakdown */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-semibold text-slate-900">Comissão Apex — cálculo por mês</h2>
            <div className="text-xs text-slate-500">
              {tiers.length > 0 && tiers.map((tier, i) => (
                <span key={i} className="inline-flex items-center gap-1 mr-3">
                  <span className="font-medium">{(tier.rate * 100).toFixed(2)}%</span>
                  <span className="text-slate-400">
                    {tier.up_to ? `até ${brl(tier.up_to)}` : "acima"}
                  </span>
                </span>
              ))}
            </div>
          </div>
          {data?.filters?.partial_month && (
            <div
              className="text-xs px-3 py-2 mb-3 rounded-md bg-amber-50 text-amber-800 border border-amber-200"
              data-testid="apex-partial-month-warning"
            >
              <strong>Filtro parcial de mês.</strong> A comissão Apex é progressiva e consumida <strong>cronologicamente</strong> dentro do mês civil (zera todo dia 01). Cada fatia pega o trecho das faixas a partir de onde os dias anteriores do mesmo mês pararam.
            </div>
          )}
          {(data?.apex_by_month?.length || 0) === 0 ? (
            <div className="text-center text-sm text-slate-400 py-8">Sem faturamento no período selecionado.</div>
          ) : (
            <div className="space-y-4">
              {data.apex_by_month.map((m) => {
                const isPartial = m.gross_full_month && Math.abs(m.gross_full_month - m.gross) > 0.01;
                const before = Number(m.gross_before_filter || 0);
                const cumStart = Number(m.cumulative_start || 0);
                const cumEnd = Number(m.cumulative_end || 0);
                return (
                <div key={m.month} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`apex-month-${m.month}`}>
                  <div className="bg-slate-50 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200">
                    <div className="font-medium text-slate-900">{monthLabel(m.month)}</div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                      <span>Faturado período: <strong className="text-slate-900">{brl(m.gross)}</strong></span>
                      {isPartial && (
                        <>
                          <span data-testid={`apex-month-${m.month}-before`}>
                            Antes do filtro: <strong className="text-slate-900">{brl(before)}</strong>
                          </span>
                          <span data-testid={`apex-month-${m.month}-full`}>
                            Mês civil: <strong className="text-slate-900">{brl(m.gross_full_month)}</strong>
                          </span>
                          <span className="w-full text-[11px] text-slate-500" data-testid={`apex-month-${m.month}-cumulative`}>
                            Faixa cronológica do período: {brl(cumStart)} → {brl(cumEnd)}
                          </span>
                        </>
                      )}
                      <span>Comissão período: <strong className="text-violet-700" data-testid={`apex-month-${m.month}-value`}>{brl(m.apex)}</strong></span>
                      {isPartial && (
                        <span>Comissão mês civil: <strong className="text-slate-600">{brl(m.apex_full_month)}</strong></span>
                      )}
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2">Faixa</th>
                        <th className="text-right px-4 py-2">Base período</th>
                        <th className="text-right px-4 py-2">Alíquota</th>
                        <th className="text-right px-4 py-2">Comissão período</th>
                        {isPartial && (
                          <>
                            <th className="text-right px-4 py-2 text-slate-400">Base mês civil</th>
                            <th className="text-right px-4 py-2 text-slate-400">Comissão mês civil</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {m.breakdown.map((b, i) => {
                        const baseFull = b.base_full_month ?? b.base;
                        const valueFull = b.value_full_month ?? b.value;
                        const dim = b.value > 0 ? "" : "text-slate-400";
                        return (
                          <tr key={i} className={`border-t border-slate-100 ${dim}`}>
                            <td className="px-4 py-2">
                              {b.from === 0 && b.to ? `Até ${brl(b.to)}` :
                               b.to ? `${brl(b.from)} a ${brl(b.to)}` :
                               `Acima de ${brl(b.from)}`}
                            </td>
                            <td className="px-4 py-2 text-right">{brl(b.base)}</td>
                            <td className="px-4 py-2 text-right">{(b.rate * 100).toFixed(2)}%</td>
                            <td className="px-4 py-2 text-right font-medium text-violet-700">{brl(b.value)}</td>
                            {isPartial && (
                              <>
                                <td className="px-4 py-2 text-right text-slate-400">{brl(baseFull)}</td>
                                <td className="px-4 py-2 text-right text-slate-400">{brl(valueFull)}</td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Por cirurgião */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <div>
              <h2 className="font-display text-lg font-semibold text-slate-900">Líquido pessoal por cirurgião</h2>
              <p className="text-xs text-slate-500 mt-0.5">Soma do que cada pessoa recebe atuando como principal + auxiliar.</p>
            </div>
            <div className="text-xs text-slate-500">{(data?.by_surgeon || []).length} pessoa(s)</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Cirurgião</th>
                <th className="text-right px-4 py-3">Casos (principal + auxiliar)</th>
                <th className="text-right px-4 py-3">Líquido pessoal</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={3} className="text-center p-6 text-slate-400">Carregando…</td></tr>}
              {!loading && (data?.by_surgeon || []).length === 0 && (
                <tr><td colSpan={3} className="text-center p-8 text-slate-400">Sem casos no período.</td></tr>
              )}
              {(data?.by_surgeon || []).map((s, idx) => (
                <tr key={s.id || s.name} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`surgeon-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                  <td className="px-4 py-3 text-right">{s.cases}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold">{brl(s.liquido)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({ icon, label, value, tone, testid }) {
  const color =
    tone === "emerald" ? "text-emerald-700" :
    tone === "amber" ? "text-amber-700" :
    tone === "rose" ? "text-rose-700" :
    tone === "violet" ? "text-violet-700" :
    tone === "cyan" ? "text-cyan-700" :
    "text-slate-900";
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5">
          {icon && <span className="h-3.5 w-3.5">{icon}</span>}
          <span>{label}</span>
        </div>
        <div className={`text-2xl font-display font-bold mt-1 ${color}`} data-testid={testid}>{value}</div>
      </CardContent>
    </Card>
  );
}
